import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Stack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NumericFormat } from "react-number-format";
import { useParams } from "react-router";

import { DocusealOverlay } from "@equidefi/portals/components/docuseal/DocusealOverlay";
import { useDocusealSigningParty } from "@equidefi/portals/hooks/useDocuseal";
import { useAgreementSigner } from "@equidefi/portals/hooks/useSignatures";
import { offeringHidesSecurityLabel, userFullName } from "@equidefi/shared";
import { Heading, Paper, Text } from "@equidefi/ui";

import GenericInfoArea from "@/components/GenericInfoArea";
import InvestmentCard from "@/components/investment/InvestmentCard";
import NavBar from "@/components/nav/NavBar";
import Spinner from "@/components/spinner";

import { WorkflowContainer } from "./WorkflowContainer";

const UnsignedScreen = ({
  agreement,
  primarySigner,
  closed = false,
  investment,
  onSign = async () => {},
  onClose = async () => {},
}) => {
  // signatureId is the Signing Party ID for Docuseal agreements
  const { investmentAgreementId, signatureId } = useParams();
  const hideSecurityLabel = offeringHidesSecurityLabel(investment?.offering);

  const usesDocuseal = agreement?.provider === "DOCUSEAL";

  const docuseal = useDocusealSigningParty(investmentAgreementId, signatureId, {
    enabled: usesDocuseal,
  });

  const handleContinue = useCallback(() => {
    if (usesDocuseal) {
      docuseal.overlay.onOpen();
    }
  }, [usesDocuseal, docuseal.overlay]);

  return (
    <WorkflowContainer
      buttonLabel="Continue to Sign"
      buttonProps={{
        display: docuseal.overlay.isOpen ? "none" : "block",
        isDisabled: closed,
        loadingText: "Please Wait",
      }}
      onContinue={handleContinue}
      title="You've Been Invited to Sign">
      <Text mb="4">
        You’ve been invited by {userFullName(primarySigner?.vault)} as an additional signer or beneficial owner in the
        following investment:
      </Text>

      {docuseal.token && (
        <DocusealOverlay
          isOpen={docuseal.overlay.isOpen}
          onClose={() => {
            onClose();
            docuseal.overlay.onClose();
          }}
          token={docuseal.token}
          src={docuseal.url}
          name={docuseal.name}
          onComplete={onSign}
          role={docuseal.role}
        />
      )}

      <Box mb={4}>
        <InvestmentCard investment={investment} hideResumeButton />
      </Box>
      <Accordion mb="4" variant="card" allowToggle>
        <AccordionItem>
          <AccordionButton>
            <Box as="span" flex="1" textAlign="left">
              <Heading my="2" textStyle="h3" fontWeight="700">
                Investment Details
              </Heading>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel>
            <Box as="ul" m={0} p={0}>
              <Flex as="li" justify="space-between" w="full">
                <Text>Investment Amount</Text>
                <NumericFormat
                  displayType="text"
                  name="amount"
                  prefix="$"
                  allowNegative={false}
                  thousandSeparator
                  value={investment?.amount}
                  fixedDecimalScale={false}
                  decimalScale={2}
                />
              </Flex>
              {!hideSecurityLabel && (
                <>
                  <Flex as="li" justify="space-between" w="full">
                    <Text>{investment?.offering?.security_type_plural}</Text>

                    <NumericFormat
                      displayType="text"
                      name="quantity"
                      allowNegative={false}
                      thousandSeparator
                      value={investment?.quantity}
                      decimalScale={2}
                      fixedDecimalScale={false}
                    />
                  </Flex>
                  <Flex as="li" justify="space-between" w="full">
                    <Text>{`${investment?.offering?.security_type_label} Price`}</Text>
                    <NumericFormat
                      displayType="text"
                      name="quantity"
                      prefix="$"
                      allowNegative={false}
                      thousandSeparator
                      value={investment?.price}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                  </Flex>
                </>
              )}
              <Flex as="li" justify="space-between" w="full">
                <Text mb={0}>Primary Signer</Text>
                <Text mb={0}>{userFullName(primarySigner?.vault)}</Text>
              </Flex>
            </Box>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
      <Text mb="4">
        As an additional signer or beneficial owner, you only need to sign the investment agreements. You can do this
        from your smartphone, computer, or tablet.
      </Text>
    </WorkflowContainer>
  );
};

const SignedScreen = ({ offeringAgreement }) => {
  return (
    <WorkflowContainer>
      <Flex align={{ base: "start", md: "center" }} justify="center" minHeight="75vh">
        <Box maxWidth="600px" width="100%">
          <Heading as="h1" fontWeight="bold" mb={2}>
            Signature Complete
          </Heading>
          <Text mb={4}>Congratulations! You have completed the signature process successfully.</Text>
          <Paper variant="primary" p={4} mb={6}>
            <Flex justify="space-between" align="center">
              <Box>
                <Heading as="h3" mb={0} textStyle="h2">
                  {offeringAgreement?.name}
                </Heading>
                <Text mb={0} fontSize="md" textStyle="context">
                  Signed
                </Text>
              </Box>
            </Flex>
          </Paper>
          <Text align="center">You can now safely close this page.</Text>
        </Box>
      </Flex>
    </WorkflowContainer>
  );
};

export const AdditionalSigner = () => {
  const { investmentAgreementId, signatureId } = useParams();

  const [closed, setClosed] = useState(false);
  const [signed, setSigned] = useState(false);

  const { data, refetch, isLoading, isError } = useAgreementSigner(investmentAgreementId, signatureId, {
    refetchInterval: closed ? 2_500 : false,
  });

  const { investment_agreement, investment, offering_agreement } = data ?? {};

  const primarySigner = useMemo(
    () => investment_agreement?.signing_parties.find((party) => party.role === "signer1"),
    [investment_agreement],
  );

  const signer = useMemo(
    () => investment_agreement?.signing_parties.find((party) => party.signature_id === signatureId),
    [investment_agreement, signatureId],
  );

  const hasSigned = useMemo(() => signed || signer?.status === "signed", [signer?.status, signed]);

  const invalidAgreement = useMemo(() => {
    return !investment_agreement || Object.keys(investment_agreement).length === 0 || isError;
  }, [investment_agreement, isError]);

  useEffect(() => {
    if (hasSigned) {
      setClosed(true);
    }
  }, [hasSigned]);

  if (!isLoading && invalidAgreement) {
    return (
      <GenericInfoArea
        heading=":("
        title="Invalid Signature Link"
        message="This signature link has expired or is no longer valid. Please contact the primary signer for an updated link."
      />
    );
  }

  const handleCloseSign = async () => {
    if (hasSigned) {
      setClosed(true);
      refetch();
    }
  };

  const handleSign = async () => {
    setSigned(true);
    handleCloseSign();
  };

  return (
    <Stack as="main" bgColor="background" direction={{ base: "column", lg: "row" }} minHeight="100vh">
      <NavBar position={hasSigned ? "8" : "1"} offering={{ ...investment?.offering, issuer: investment?.issuer }}>
        {isLoading ? (
          <Spinner show={isLoading} />
        ) : hasSigned ? (
          <SignedScreen offeringAgreement={offering_agreement} />
        ) : (
          <UnsignedScreen
            primarySigner={primarySigner}
            investment={investment}
            agreement={investment_agreement}
            closed={closed}
            onClose={handleCloseSign}
            onSign={handleSign}
          />
        )}
      </NavBar>
    </Stack>
  );
};
